/**
 * Terms Acceptance Service
 * Handles the submission and retrieval of terms acceptance data
 */

import { api } from './api';
import logger from '../utils/logger';

const TERMS_ACCEPTANCE_KEY = 'rely_terms_acceptance';

/**
 * Get stored terms acceptance from localStorage
 * This is set by the signup page when user checks the checkbox
 * @returns {Object|null} Terms acceptance data or null
 */
export const getStoredTermsAcceptance = () => {
  try {
    const stored = localStorage.getItem(TERMS_ACCEPTANCE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    logger.error('Error reading terms acceptance from localStorage:', error);
    return null;
  }
};

/**
 * Clear stored terms acceptance from localStorage
 */
export const clearStoredTermsAcceptance = () => {
  try {
    localStorage.removeItem(TERMS_ACCEPTANCE_KEY);
  } catch (error) {
    logger.error('Error clearing terms acceptance from localStorage:', error);
  }
};

/**
 * Submit terms acceptance to the backend
 * This should be called after successful login/signup
 * @param {Object} acceptanceData - Optional override data
 * @returns {Promise<Object>} Response from backend
 */
export const submitTermsAcceptance = async (acceptanceData = null) => {
  // Get data from localStorage if not provided
  const data = acceptanceData || getStoredTermsAcceptance();

  if (!data) {
    logger.warn('No terms acceptance data found');
    return null;
  }

  try {
    const response = await api.post('/api/user/accept-terms', {
      termsVersion: data.termsVersion,
      privacyVersion: data.privacyVersion,
      userAgent: data.userAgent,
      acceptedAt: data.acceptedAt,
    });

    // Clear localStorage after successful submission
    clearStoredTermsAcceptance();

    logger.debug('Terms acceptance submitted successfully');
    return response.data;
  } catch (error) {
    logger.error('Error submitting terms acceptance:', error);
    // Don't throw - terms acceptance failure shouldn't block the user
    return null;
  }
};

/**
 * Get the user's terms acceptance status from the backend
 * @returns {Promise<Object>} Terms status
 */
export const getTermsStatus = async () => {
  try {
    const response = await api.get('/api/user/terms-status');
    return response.data;
  } catch (error) {
    logger.error('Error fetching terms status:', error);
    return {
      hasAcceptedTerms: false,
      acceptedAt: null,
      termsVersion: null,
      privacyVersion: null,
    };
  }
};

/**
 * Check if user needs to accept new terms
 * Compares stored acceptance with current terms version
 * @param {string} currentTermsVersion - Current terms version
 * @param {string} currentPrivacyVersion - Current privacy version
 * @returns {Promise<boolean>} True if user needs to accept new terms
 */
export const needsTermsAcceptance = async (
  currentTermsVersion,
  currentPrivacyVersion
) => {
  try {
    const status = await getTermsStatus();

    if (!status.hasAcceptedTerms) {
      return true;
    }

    // Check if versions have changed
    return (
      status.termsVersion !== currentTermsVersion ||
      status.privacyVersion !== currentPrivacyVersion
    );
  } catch (error) {
    logger.error('Error checking terms acceptance:', error);
    return false; // Don't block user on error
  }
};

export default {
  getStoredTermsAcceptance,
  clearStoredTermsAcceptance,
  submitTermsAcceptance,
  getTermsStatus,
  needsTermsAcceptance,
};
